import { useCallback, useRef } from 'react';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { DragEvent, RectDrawControls, useDollhouse } from '@spazious/dollhouse-engine';
import { useNavigate } from '@spazious/dollhouse-router';
import { FShallowNode } from '@spazious/dollhouse-shallow-graph';
import { useStaging, useStore } from '@spazious/dollhouse-store';
import { ToolModeEnum } from '@spazious/dollhouse-types';

import { ROUTE_PATH_INSPECTOR } from '../../constants/index.js';
import { useDelayedSelection, useDrawFsetup } from '../../hooks/index.js';

export const DrawFsetupControls = () => {
  const { staging, createAction } = useStaging();
  const set = useStore(({ set }) => set);
  const { node } = useDrawFsetup();
  const { set: setDrawFsetup } = useDrawFsetup();
  const navMeshQuery = useDollhouse(({ world }) => world?.navMeshQuery);
  const selector = useRef<string | null>(null);
  const navigate = useNavigate();

  const { setSelectors } = useDelayedSelection((pendingSelectors) => {
    if (!pendingSelectors.length) {
      set({
        showNavMesh: false,
        tool: ToolModeEnum.None,
      });
      navigate(ROUTE_PATH_INSPECTOR);
    }
  });

  const handleDraw = useCallback(
    (event: DragEvent) => {
      const { center, size, originalEvent } = event;
      if (!node) {
        console.warn('Trying to draw without selecting a arrangement');
        return;
      }
      switch (originalEvent.type) {
        case 'pointerdown':
          staging.prepare(true);
          const action = createAction('add', node as FShallowNode);
          staging.add(action);
          selector.current = action.payload.selector;
          break;

        case 'pointermove':
          if (!selector.current) {
            return;
          }
          const actions = [];
          actions.push(createAction('update', selector.current, 'position', center));
          actions.push(createAction('update', selector.current, 'attrs.size', size));
          staging.add(actions);

          break;

        case 'pointerup':
          if (staging.stack.length) {
            staging.commit(CommanderActionTypesEnum.Add);
          }
          setDrawFsetup({ node: null });
          if (selector.current) {
            setSelectors([selector.current]);
          } else {
            set({
              showNavMesh: false,
              tool: ToolModeEnum.None,
            });
          }
          selector.current = null;
          break;

        default:
          break;
      }
    },
    [createAction, node, set, setDrawFsetup, setSelectors, staging]
  );

  return <RectDrawControls color="#6c81d3" navMeshQuery={navMeshQuery} onDraw={handleDraw} />;
};

export const DrawFsetup = () => {
  const tool = useStore(({ tool }) => tool);
  return tool === ToolModeEnum.DrawFsetup ? <DrawFsetupControls /> : null;
};

export default DrawFsetup;
